import {ChangeEvent, useState, useEffect} from 'react'
import {useParams} from 'react-router-dom'
import cn from 'clsx'
import {Input as AntdInput, Spin, Upload} from 'antd'

import './Chat.scss'
import {queryClient} from 'base'
import {useGetRequest} from 'modules/request'
import {useGetProfile} from 'modules/profile'
import {
  useUploadFile,
  useGetNotifications,
  useReadNotificationsCategory,
} from 'common/api'
import {useMount} from 'common/hooks'
import {
  Block,
  Text,
  File,
  Loader,
  UserAvatar,
  IconButton,
  ClipAttachmentIcon,
  ArrowTopFillIcon,
} from 'common/components'
import {formatDate} from 'common/helpers'
import {FileInterface, BlobInterface} from 'common/types'
import {useGetChat, useSendMessage} from '.'

interface ChatProps {
  isRequest: boolean
}

interface ContentProps {
  chat: any
  profileUuid: string
}

interface MessageProps {
  self?: boolean
  prevSameSender: boolean
  item: any
  sender: any
}

interface UploadButtonProps {
  isUploading: boolean
  disabled: boolean
  uploadFile: (blob: BlobInterface) => void
}

interface InputProps {
  projectUuid: string
  chatUuid?: string
  profileUuid: string
  disabled: boolean
}

const contentId = 'chatContent'

const isCurrentChatNotification = (notification: any, projectUuid: string) =>
  notification.data.project.uuid === projectUuid &&
  notification.type === 'newChatMessage'

const handleScrollContentDown = (delay: number = 10) =>
  setTimeout(() => {
    const contentElement = document.getElementById(contentId)
    if (!contentElement) return
    contentElement.scrollTop = contentElement.scrollHeight
  }, delay)

const Message = ({self, prevSameSender, item, sender}: MessageProps) => {
  const {attachment, message} = item

  const showInfo = !prevSameSender && item.sentAt

  return (
    <div
      className={cn(
        'Chat-Message',
        prevSameSender && 'Chat-Message_sameSender'
      )}
    >
      <div>
        <div className="Chat-Wrapper">
          <div className="Chat-AvatarWrapper">
            {showInfo && (
              <UserAvatar
                className="Chat-Avatar"
                size={24}
                src={sender ? sender.photo : null}
                alt={sender ? sender.firstName : 'User'}
              />
            )}
          </div>
          <div className="Chat-Block">
            {!item.sentAt && <Spin className="Chat-TempSpin" size="small" />}
            {attachment ? (
              <File
                bordered
                name={attachment.name}
                userName={
                  sender ? `${sender.firstName} ${sender.lastName}` : undefined
                }
                size={attachment.size}
                url={attachment.url}
              />
            ) : (
              <Block
                as="p"
                className="Chat-Text"
                type={self ? 'grey' : undefined}
              >
                {message}
              </Block>
            )}
          </div>
        </div>
      </div>
      <div className="Chat-DateWrapper">
        {showInfo && (
          <Text noWrap color="grey" size="xs">
            {formatDate(item.sentAt, 'nineth')}
          </Text>
        )}
      </div>
    </div>
  )
}

const UploadButton = ({
  isUploading,
  disabled,
  uploadFile,
}: UploadButtonProps) => {
  const hanldeUpload = ({file}: any) => uploadFile(file)

  return (
    <Upload
      name="file"
      showUploadList={false}
      disabled={isUploading}
      customRequest={hanldeUpload}
    >
      <IconButton
        loading={isUploading}
        icon={<ClipAttachmentIcon />}
        disabled={disabled}
      />
    </Upload>
  )
}

const Input = ({projectUuid, chatUuid, profileUuid, disabled}: InputProps) => {
  const [message, setMessage] = useState('')
  const {mutate: sendMessage, isMutating: isSending} = useSendMessage(
    projectUuid,
    chatUuid
  )
  const {mutate: uploadFile, isMutating: isUploading} = useUploadFile({
    setFile: (file: FileInterface) => {
      const tempId = Date.now()
      sendMessage({
        tempId,
        attachment: file.uuid,
      })
      const chatQueryKey = ['chat', projectUuid]
      queryClient.cancelQueries(chatQueryKey)
      queryClient.setQueriesData(chatQueryKey, (oldData: any) => {
        const newData = {
          ...oldData,
          messages: oldData.messages.slice(),
        }
        newData.messages.push({
          id: tempId,
          senderUuid: profileUuid,
          attachment: {
            name: file.blob.name,
            size: file.blob.size,
            type: file.blob.type,
            url: file.url,
          },
          sentAt: null,
        })
        return newData
      })
      handleScrollContentDown()
    },
  })

  const handleMessageSend = () => {
    const tempId = Date.now()
    const trimmedMessage = message.trim()
    sendMessage({
      message: trimmedMessage || undefined,
      tempId,
    })
    const chatQueryKey = ['chat', projectUuid]
    queryClient.cancelQueries(chatQueryKey)
    queryClient.setQueriesData(chatQueryKey, (oldData: any) => {
      const newData = {
        ...oldData,
        messages: oldData.messages.slice(),
      }
      newData.messages.push({
        message,
        id: tempId,
        senderUuid: profileUuid,
        attachment: undefined,
        sentAt: null,
      })
      return newData
    })
    setMessage('')
    handleScrollContentDown()
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value)
  }

  const submitDisabled = disabled || isSending || isUploading || !message.trim()

  return (
    <AntdInput
      autoFocus
      className="Chat-Input"
      placeholder="Write a message…"
      value={message}
      prefix={
        <UploadButton
          isUploading={isUploading}
          disabled={isSending || disabled}
          uploadFile={uploadFile}
        />
      }
      suffix={
        <IconButton
          icon={<ArrowTopFillIcon />}
          disabled={submitDisabled}
          onClick={handleMessageSend}
        />
      }
      onChange={handleInputChange}
      onPressEnter={submitDisabled ? undefined : handleMessageSend}
    />
  )
}

const Content = ({chat, profileUuid}: ContentProps) => {
  const isMounted = useMount()

  useEffect(() => {
    if (isMounted) return
    handleScrollContentDown(0)
  }, []) // eslint-disable-line

  const {messages, members} = chat

  if (!messages.length)
    return (
      <Text className="Chat-Empty" align="center" color="grey">
        No messages here yet...
      </Text>
    )

  return (
    <div id={contentId} className="Chat-Content">
      {messages.map((item: any, index: number) => (
        <Message
          key={item.id}
          self={profileUuid === item.senderUuid}
          prevSameSender={
            messages[index - 1]
              ? messages[index - 1].senderUuid === item.senderUuid
              : false
          }
          item={item}
          sender={members.find(
            (member: any) => member.uuid === item.senderUuid
          )}
        />
      ))}
    </div>
  )
}

export default function Chat({isRequest}: ChatProps) {
  const params = useParams()
  const {data: request} = useGetRequest(params.uuid, isRequest)
  const projectUuid = isRequest ? request.project.uuid : params.uuid
  const {data: chat} = useGetChat(projectUuid)
  const {data: profile} = useGetProfile()
  const {data: notifications} = useGetNotifications()
  const {mutate: readNotificationsCategory} =
    useReadNotificationsCategory('chat')

  const hasUpdates = notifications?.some((notification: any) =>
    isCurrentChatNotification(notification, projectUuid)
  )

  useEffect(() => {
    if (!hasUpdates) return
    const chatNotifications = notifications
      .filter((notification: any) =>
        isCurrentChatNotification(notification, projectUuid)
      )
      .map((notification: any) => notification.uuid)
    readNotificationsCategory(chatNotifications)
  }, [hasUpdates]) // eslint-disable-line

  return (
    <div>
      <div className="Chat-Container">
        {chat ? (
          <Content chat={chat} profileUuid={profile.uuid} />
        ) : (
          <Loader className="Chat-Loader" />
        )}
      </div>
      <Input
        projectUuid={projectUuid}
        chatUuid={chat?.uuid}
        profileUuid={profile.uuid}
        disabled={!chat}
      />
    </div>
  )
}
